import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { createEnumParam, StringParam, withDefault } from 'use-query-params'

import useFiltering from '@shared/hooks/src/useFiltering'
import { queryClient, setQueriesData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { filterCache, flatten, mapCache, pageParam, QK } from '@shared/utils'

import useQPhraseUsage from '@hooks/useQPhrasesUsage'

export const LIMIT = 50

const ViewParam = createEnumParam(['personal', 'team'])

export function useQPhrasesFilters() {
  return useFiltering({
    phrases: withDefault(ViewParam, 'personal'),
    phrasesSearch: StringParam,
    phraseId: StringParam,
  })
}

export function useQPhrases() {
  const [{ phrases, phrasesSearch }] = useQPhrasesFilters()
  const { stats } = useQPhraseUsage()

  const query = {
    limit: LIMIT,
    category: phrases,
    search_term: phrasesSearch,
    status: 'active',
  }

  return useInfiniteQuery({
    queryKey: QK.qPhrases.list(query),
    queryFn: ({ pageParam }) => API.snippets.list({ ...query, offset: pageParam * LIMIT }),
    select: (data) => flatten(data).sort((a, b) => (stats[b.id] || 0) - (stats[a.id] || 0)),
    initialPageParam: 0,
    getNextPageParam: pageParam(LIMIT),
  })
}

export function useQPhraseCreation(config) {
  return useMutation({
    mutationFn: (data) => API.snippets.create(data, config),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QK.qPhrases.lists }),
  })
}

export function useQPhraseUpdate(config) {
  return useMutation({
    mutationFn: ({ id, data }) => API.snippets.id(id).update(data, config),
    onSuccess: (updatedPhrase, { id }) =>
      setQueriesData(
        { queryKey: QK.qPhrases.lists },
        mapCache((phrase) => (phrase.id === id ? updatedPhrase : phrase))
      ),
  })
}

export function useQPhraseDelete() {
  return useMutation({
    mutationFn: (id) => API.snippets.id(id).setStatus({ status: 'deleted' }),
    onSuccess: (updatedPhrase, id) =>
      setQueriesData(
        { queryKey: QK.qPhrases.lists },
        filterCache((phrase) => phrase.id !== id)
      ),
  })
}
